import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { shallowEqual, useDispatch, useSelector, useStore } from "react-redux";
import { useGrowthBook } from "@growthbook/growthbook-react";
import { AppState } from "@Store/index";
import config from "@Config/index";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES } from "constants/index";
import { isPaginatedUrl } from "../SearchList/useSearchList";
import {
  getRecommendationEventContext,
  planRecommendations,
} from "../../data/recommendationsThunks";
import { hasStandardFilter, tokensFromFilters } from "../../utils/filterApplicationOrder";
import {
  getExpandAvailableCount,
  isGeoRadiusExpandFlow,
  resolveRadiusExpandEnabled,
} from "../../utils/geoRadiusExpand";
import { RECOMMENDATIONS_PAGE_SIZE } from "../../utils/relaxation";
import { initialRecommendations } from "../../data/searchPageReducer";
import { RecommendationCardContext } from "../SearchListItem/types";
import { isAvailable } from "../SearchListItem/helper";
import { SEARCH_RECOMMENDATIONS_SOURCE } from "@Utils/searchRecommendationAttribution";

type Params = { isDesktop: boolean };

/**
 * Eligibility, trigger and infinite-scroll paging for the recommendation section (P1D-5464).
 * Planning starts the moment the main list reports no next page (HLD D5).
 */
const useRecommendations = ({ isDesktop }: Params) => {
  const dispatch = useDispatch();
  const store = useStore<AppState>();
  const platform = isDesktop ? "desktop" : "mobile";

  const {
    listReadyStatus,
    next,
    meta,
    filters,
    region,
    isAgentViewOpened,
    isRadiusExpanded,
    radiusExpandEnabled,
    recommendations,
    mainItems,
  } = useSelector(
    ({ searchPageData }: AppState) => ({
      listReadyStatus: searchPageData.searchList.readyStatus,
      next: searchPageData.searchList.next,
      meta: searchPageData.searchList.meta,
      filters: searchPageData.filters,
      region: searchPageData.region,
      isAgentViewOpened: searchPageData.isAgentViewOpened,
      isRadiusExpanded: searchPageData.isRadiusExpanded,
      radiusExpandEnabled: searchPageData.radiusExpandEnabled,
      recommendations: searchPageData.recommendations,
      mainItems: searchPageData.searchList.items,
    }),
    shallowEqual,
  );
  // `recommendations` is absent when hydrating HTML cached before this feature shipped.
  const { status, items, pageLoading, stepOfItem, plan } =
    recommendations ?? initialRecommendations;
  const mainItemsLength = mainItems?.length || 0;

  // `useFeatureIsOn` does not reliably re-render when the payload lands, so wait on the load
  // promise ourselves and re-run eligibility from our own state change.
  const growthbook = useGrowthBook();
  const [growthbookReady, setGrowthbookReady] = useState(() => Boolean(growthbook?.ready));
  useEffect(() => {
    // China renders the provider but never initialises GrowthBook: do not fetch from there.
    if (growthbookReady || !growthbook || config.IS_CHINA_APP) return undefined;
    let cancelled = false;
    growthbook
      .loadFeatures()
      .then(() => !cancelled && setGrowthbookReady(true))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [growthbook, growthbookReady]);

  const flagOn = Boolean(
    growthbookReady &&
      growthbook?.isOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.SEARCH_RECOMMENDED_BEYOND_FILTERS),
  );
  // GrowthBook never initialises on the China build, so China shows the section unflagged.
  const switchedOn = flagOn || config.IS_CHINA_APP;

  // FR-4: with a geo filter, the "view nearby" flow runs first. Wait while it can still expand.
  const geoFlow = isGeoRadiusExpandFlow(
    filters,
    region,
    resolveRadiusExpandEnabled(radiusExpandEnabled),
  );
  const nearbyPending = geoFlow && !isRadiusExpanded && getExpandAvailableCount(meta) > 0;

  const exhausted = listReadyStatus === "success" && (next == null || next === 0);
  const eligible =
    switchedOn &&
    exhausted &&
    !nearbyPending &&
    !isAgentViewOpened &&
    !isPaginatedUrl() &&
    hasStandardFilter(filters);

  useEffect(() => {
    if (eligible && status === "idle") dispatch(planRecommendations());
  }, [eligible, status, dispatch]);

  const visible = switchedOn && (status === "ready" || status === "exhausted") && items.length > 0;

  // List C total for breaker placement (P1D-5517 FR-6). Keep it once the last page has loaded
  // ("exhausted"): dropping to 0 there removed the section's breaker after cards 11–15 (P1D-5525).
  const listCTotal = useMemo(() => {
    if ((status !== "ready" && status !== "exhausted") || !plan.length) return 0;
    return Math.min(
      15,
      plan.reduce((sum, step) => sum + (step.pool || 0), 0),
    );
  }, [status, plan]);

  // Infinite scroll (HLD D14) is `useSearchList`'s scroll handler: the section renders inside
  // the list it measures, and `loadMoreRecommendationsIfNeed` pages it once the list is exhausted.

  // FR-43 (v5): one event per appended page after the first.
  const reportedLength = useRef(0);
  useEffect(() => {
    if (reportedLength.current && items.length > reportedLength.current) {
      gtagEventEmitter({
        action: "search_recommendations_page_loaded",
        ...getRecommendationEventContext(store.getState(), platform),
        loaded_count_before: reportedLength.current,
        page_index: Math.ceil(items.length / RECOMMENDATIONS_PAGE_SIZE),
      });
    }
    reportedLength.current = items.length;
  }, [items.length, platform, store]);

  const onSectionSeen = useCallback(() => {
    gtagEventEmitter({
      action: "search_recommendations_section_viewed",
      ...getRecommendationEventContext(store.getState(), platform),
      accepted_step_count: plan.length,
      pool_size: plan.reduce((sum, step) => sum + step.pool, 0),
    });
  }, [plan, platform, store]);

  const cardContextFor = useCallback(
    (item: any, position: number): RecommendationCardContext => {
      const stepIndex = stepOfItem[String(item?.id)] ?? plan[plan.length - 1]?.index ?? 1;
      const step = plan.find((s) => s.index === stepIndex) || plan[plan.length - 1];
      const removed = step?.removedTokens || [];
      return {
        ...getRecommendationEventContext(store.getState(), platform),
        source: SEARCH_RECOMMENDATIONS_SOURCE,
        inventory_id: item?.id,
        is_sold_out: !isAvailable(item),
        position,
        step_index: stepIndex,
        removed_filters: removed,
        remaining_filters: tokensFromFilters(filters).filter((t) => !removed.includes(t)),
      };
    },
    [stepOfItem, plan, platform, filters, store],
  );

  return {
    visible,
    items,
    pageLoading,
    mainItemsLength,
    onSectionSeen,
    cardContextFor,
    listCTotal,
  };
};

export default useRecommendations;
